import { ChangeEvent, Dispatch, useEffect, useReducer, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { SelectChangeEvent } from '@mui/material/Select';
import dayjs from 'dayjs';

import { CustomCheckBox, CustomInput, CustomSelect, MultipleSelect, Text } from '../../../shared/uiComponents';
import { validateName } from '../../../shared/Helpers/functions';
import { BT_LOCATION_MISMATCH_ERROR } from '../../../shared/Helpers/constants';
import { AdminTypes } from '../../../components/Menu';
import { InfoRow } from '../../../components/StyledComponents';
import { useBehaviorTreatmentOptions } from '../hook';
import {
	ACTIONS,
	ActionProperties,
	ERRORS,
	behaviorTreatmentErrorReducer,
	initialBehaviorTreatmentErrorState,
} from './behaviorTreatmentReducer';
import { Behaviors, SignatureInput, Targets, TimeInfo } from '../components';
import { useSoapNoteFieldDisabled } from '../helpers';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { getSupervisorSessionsInfo } from '../../../redux/State/clientSlice/sessionSlice';
import {
	LocationTypes,
	UpdateBehaviorTreatmentProperties,
} from '../../../redux/API/ClientAPIHelpers/soapNoteProperties';
import { SoapNotesStatuses } from '../../../redux/API/ClientAPIHelpers/dataCollectionProperties';

const frequencyAndDurationDescription = `Describe frequency, duration and topography of maladaptive behavior.
Describe techniques used as interventions for the behaviors.
State if no maladaptive behaviors occurred.`;

const BehaviorTreatmentData = ({
	data,
	setData,
	save,
}: {
	data: UpdateBehaviorTreatmentProperties;
	save: () => void;
	setData: Dispatch<ActionProperties>;
}) => {
	const today = new Date();
	const todayString = dayjs(today).format('MM/DD/YYYY');

	const dispatch = useDispatch<DispatchProperties>();
	const [params] = useSearchParams();
	const { user } = useSelector((state) => state.session.sessionInfo);

	const locationList = useSelector((state) => state.soapNote.locations);
	const supervisor = useSelector((state) => state.soapNote.soapNoteData.supervisor);
	const supervisorSessionInfo = useSelector((state) => state.session.supervisorSessionInfo);
	const errorList = useSelector((state) => state.soapNote.error?.errors);

	const { getFieldDisabled } = useSoapNoteFieldDisabled();

	const [locations, setLocations] = useState<LocationTypes[]>([]);
	const [errors, setErrors] = useReducer(behaviorTreatmentErrorReducer, initialBehaviorTreatmentErrorState);

	const {
		barriersList,
		technuquiesList,
		behaviorTreatmentFutureList,
		clientMoodList,
		clientResponseToTreatmentList,
		levelOfMaladaptiveBehaviorList,
		maladaptiveBehaviorTechniquesList,
		skillDomainAddressedList,
		reinforsmentList,
		participantsList,
	} = useBehaviorTreatmentOptions();

	useEffect(() => {
		if (!locationList) return;

		if (!supervisorSessionInfo.length) {
			setLocations(locationList);

			return;
		}

		// Only filter locations if DSU note is submitted /pendingBT
		const submittedSupervisorSessions = supervisorSessionInfo.filter(
			(x) =>
				x.reportStatus?.status === SoapNotesStatuses.submitted ||
				x.reportStatus?.status === SoapNotesStatuses.pendingBT,
		);

		if (!submittedSupervisorSessions.length) {
			setLocations(locationList);

			return;
		}

		const supervisorLocations = submittedSupervisorSessions.map((x) => x.location);
		const filteredLocationList = locationList.filter((x) =>
			supervisorLocations.find((y) => y.locationType === x.locationType),
		);
		setLocations(filteredLocationList);
	}, [locationList, supervisorSessionInfo]);

	useEffect(() => {
		if (data.participantResponses.length) return;
		const payload = participantsList.filter((x) => x.isDefault).map((y) => y.id);
		setData({ type: ACTIONS.setParticipants, payload });
	}, [participantsList, setData, data]);

	useEffect(() => {
		const sessionId = params.get('sessionId');
		if (!sessionId) return;
		dispatch(getSupervisorSessionsInfo(sessionId));
	}, [dispatch, params]);

	useEffect(() => {
		if (!errorList) return;
		const list = Object.keys(errorList);
		list.forEach((id) => setErrors({ type: ERRORS[id], payload: true }));
	}, [errorList]);

	const levelOfbehaviorHandler = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setLevelOfMaladaptiveBehavior });
		if (!errors.LevelOfMaladaptiveBehavior) return;
		setErrors({ payload: false, type: ERRORS.LevelOfMaladaptiveBehavior });
	};

	const responseToTreatmentHandler = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setClientResponseToTreatment });
		if (!errors.ClientResponseToTreatment) return;
		setErrors({ payload: false, type: ERRORS.ClientResponseToTreatment });
	};

	const describeSessionHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setSessionDescription });
		if (!errors.SessionDescription) return;
		setErrors({ payload: false, type: ERRORS.SessionDescription });
	};

	const barrierToTreatmentOtherHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setBarrierToTreatmentOther });
		if (!errors.BarrierToTreatmentOther) return;
		setErrors({ payload: false, type: ERRORS.BarrierToTreatmentOther });
	};

	const clientMoodOtherHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setClientMoodOther });
		if (!errors.ClientMoodOther) return;
		setErrors({ payload: false, type: ERRORS.ClientMoodOther });
	};

	const futurePlanOtherHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setFuturePlanOther });
		if (!errors.FuturePlanOther) return;
		setErrors({ payload: false, type: ERRORS.FuturePlanOther });
	};

	const reinforcersOtherHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setReinforcementOther });
		if (!errors.ReinforcementOther) return;
		setErrors({ payload: false, type: ERRORS.ReinforcementOther });
	};

	const howBarrierAffectHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setHowBarrierAffect });
		if (!errors.HowBarrierAffect) return;
		setErrors({ payload: false, type: ERRORS.HowBarrierAffect });
	};

	const workedGoalsHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setWorkedGoals });
		if (!errors.WorkedGoals) return;
		setErrors({ payload: false, type: ERRORS.WorkedGoals });
	};

	const describeFrequencyAndDurationHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({
			payload: value,
			type: ACTIONS.setFrequencyAndDurationOfMaladaptiveBehaviors,
		});
		if (!errors.FrequencyAndDurationOfMaladaptiveBehaviors) return;
		setErrors({
			payload: false,
			type: ERRORS.FrequencyAndDurationOfMaladaptiveBehaviors,
		});
	};

	const barriersTreatmentHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({ type: ACTIONS.setBarrierToTreatmentResponses, payload: value });
		if (!errors.BarrierToTreatmentResponses) return;
		setErrors({ payload: false, type: ERRORS.BarrierToTreatmentResponses });
	};

	const behaviorTechniquesHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({
			payload: value,
			type: ACTIONS.setBehaviorTreatmentTechniquesResponses,
		});
		if (!errors.BehaviorTreatmentTechniquesResponses) return;
		setErrors({
			payload: false,
			type: ERRORS.BehaviorTreatmentTechniquesResponses,
		});
	};

	const reinforcerHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({ type: ACTIONS.setReinforcementResponses, payload: value });
		if (!errors.Reinforcement) return;
		setErrors({ payload: false, type: ERRORS.Reinforcement });
	};

	const malaptiveTechniquesHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({
			payload: value,
			type: ACTIONS.setMaladaptiveTechniquesResponses,
		});
		if (!errors.MaladaptiveBehaviorTechniquesResponses) return;
		setErrors({
			payload: false,
			type: ERRORS.MaladaptiveBehaviorTechniquesResponses,
		});
	};

	const skillDomainHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({
			payload: value,
			type: ACTIONS.setSkillDomainAddressed,
		});
		if (!errors.SkillDomainAddressedResponses) return;
		setErrors({ payload: false, type: ERRORS.SkillDomainAddressedResponses });
	};

	const clientMoodHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({ payload: value, type: ACTIONS.setClientMoodResponses });
		if (!errors.ClientMoodResponses) return;
		setErrors({ payload: false, type: ERRORS.ClientMoodResponses });
	};

	const futurePlansHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({ payload: value, type: ACTIONS.setFuturePlanResponses });
		if (!errors.BehaviorTreatmentFuturePlanResponses) return;
		setErrors({
			payload: false,
			type: ERRORS.BehaviorTreatmentFuturePlanResponses,
		});
	};

	const locationHandler = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setLocationId });
		if (!errors.LocationId) return;
		setErrors({ payload: false, type: ERRORS.LocationId });
	};

	const signatureHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value.toUpperCase(), type: ACTIONS.setSignature });
		if (!errors.Signature) return;
		setErrors({ payload: false, type: ERRORS.Signature });
	};

	const validateSignatureHandler = () => {
		if (validateName(data.signature)) {
			save();

			return;
		}
		setErrors({ payload: true, type: ERRORS.Signature });
	};

	const participantsHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({ type: ACTIONS.setParticipants, payload: value });
		if (!errors.ParticipantResponses) return;
		setErrors({ payload: false, type: ERRORS.ParticipantResponses });
	};

	const participantOtherHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setBehaviorTreatmentParticipantOther });
		if (!errors.BehaviorTreatmentParticipantOther) return;
		setErrors({ payload: false, type: ERRORS.BehaviorTreatmentParticipantOther });
	};

	const isSupervisedHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { checked } = event.target;
		setData({
			type: ACTIONS.setIsSessionSupervised,
			payload: checked,
		});
	};

	const isOtherSelectedForBarriersToTreatment = data.barrierToTreatmentResponses.find(
		(x) => barriersList.find((y) => y.id === x)?.text === 'Other',
	);

	const isOtherSelectedForFuturePlans = data.behaviorTreatmentFuturePlanResponses.find(
		(x) => behaviorTreatmentFutureList.find((y) => y.id === x)?.text === 'Other',
	);

	const isOtherSelectedForClientMood = data.clientMoodResponses.find(
		(x) => clientMoodList.find((y) => y.id === x)?.text === 'Other',
	);

	const isOtherSelectedForReinforcers = data.reinforcementResponses.find(
		(x) => reinforsmentList.find((y) => y.id === x)?.text === 'Other',
	);

	const isOtherSelectedForParticipants = data.participantResponses.find(
		(x) => participantsList.find((y) => y.id === x)?.text === 'Other',
	);

	return (
		<>
			<TimeInfo disabled={getFieldDisabled('startTime')} save={save} />
			{user.role.section.id === AdminTypes.bt && (
				<CustomInput label="Behavior Technician:" value={user.fullName} className="marginBottom16" disabled />
			)}
			{Boolean(supervisorSessionInfo.length) && Boolean(supervisor) && (
				<div className="padding8" style={{ minWidth: '300px' }}>
					<Text title="Supervised session info:" type="plg" fontWeight="semiBold" />
					<InfoRow>
						<Text title="Supervisor:" type="plg" fontWeight="semiBold" />
						<Text title={supervisor?.fullName ?? ''} />
					</InfoRow>
					{supervisorSessionInfo.map((x) => (
						<>
							{Boolean(x.location) && (
								<InfoRow>
									<Text title="Location:" type="plg" fontWeight="semiBold" />
									<Text type="plg" fontWeight="medium" title={x.location.name} />
								</InfoRow>
							)}
							<InfoRow>
								<Text title="Date:" type="plg" fontWeight="semiBold" />
								<Text type="plg" fontWeight="medium" title={`${dayjs(x.date).utc(false).format('MM/DD/YYYY')}`} />
							</InfoRow>
							<InfoRow>
								<Text title="Start time:" type="plg" fontWeight="semiBold" />
								<Text type="plg" fontWeight="medium" title={`${dayjs(x.startTime).utc(false).format('hh:mm A')}`} />
							</InfoRow>
							<InfoRow>
								<Text title="End time:" type="plg" fontWeight="semiBold" />
								<Text type="plg" fontWeight="medium" title={`${dayjs(x.endTime).utc(false).format('hh:mm A')}`} />
							</InfoRow>
						</>
					))}
				</div>
			)}
			<CustomSelect
				label="Location:"
				data={locations}
				value={data.locationId ? data.locationId : ''}
				error={errors.LocationId}
				errorMessage={errors.LocationId ? BT_LOCATION_MISMATCH_ERROR : ''}
				setValue={locationHandler}
				className="marginBottom16"
				disabled={getFieldDisabled('locationId')}
				onBlur={save}
			/>
			<CustomCheckBox
				style={{
					marginBottom: '16px',
				}}
				item={{
					label: 'Session was supervised by the BCBA',
					checked: data.isSessionSupervised,
					id: '0',
					disabled: getFieldDisabled('isSessionSupervised'),
				}}
				onChange={isSupervisedHandler}
				onBlur={save}
			/>
			<MultipleSelect
				label="Session Participants:"
				data={participantsList}
				initialValue={data.participantResponses}
				setValue={participantsHandler}
				error={errors.ParticipantResponses}
				className="marginBottom16"
				disabled={getFieldDisabled('participantResponses')}
				onBlur={save}
			/>
			{isOtherSelectedForParticipants && (
				<CustomInput
					label="If other, please describe: "
					value={data.behaviorTreatmentParticipantOther}
					onChange={participantOtherHandler}
					className="marginBottom16"
					multiline
					disabled={getFieldDisabled('behaviorTreatmentParticipantOther')}
					onBlur={save}
					error={errors.BehaviorTreatmentParticipantOther}
				/>
			)}
			<CustomInput
				label="Describe the session: "
				value={data.sessionDescription}
				onChange={describeSessionHandler}
				className="marginBottom16"
				multiline
				disabled={getFieldDisabled('sessionDescription')}
				onBlur={save}
			/>
			<MultipleSelect
				label="Barriers to Treatment:"
				data={barriersList}
				setValue={barriersTreatmentHandler}
				className="marginBottom16"
				initialValue={data.barrierToTreatmentResponses}
				disabled={getFieldDisabled('barrierToTreatmentResponses')}
				onBlur={save}
			/>
			{isOtherSelectedForBarriersToTreatment && (
				<CustomInput
					label="If other, please describe: "
					value={data.barrierToTreatmentOther}
					onChange={barrierToTreatmentOtherHandler}
					className="marginBottom16"
					multiline
					disabled={getFieldDisabled('barrierToTreatmentOther')}
					onBlur={save}
					error={errors.BarrierToTreatmentOther}
				/>
			)}
			<CustomInput
				label="How did the above barrier affect the session? "
				value={data.howBarrierAffect}
				onChange={howBarrierAffectHandler}
				className="marginBottom16"
				multiline
				disabled={getFieldDisabled('howBarrierAffect')}
				onBlur={save}
			/>
			<MultipleSelect
				label="Skill domains addressed: "
				data={skillDomainAddressedList}
				setValue={skillDomainHandler}
				initialValue={data.skillDomainAddressedResponses}
				className="marginBottom16"
				disabled={getFieldDisabled('skillDomainAddressedResponses')}
				onBlur={save}
			/>
			<MultipleSelect
				label="Techniques Used: "
				data={technuquiesList}
				setValue={behaviorTechniquesHandler}
				initialValue={data.behaviorTreatmentTechniquesResponses}
				className="marginBottom16"
				disabled={getFieldDisabled('behaviorTreatmentTechniquesResponses')}
				onBlur={save}
			/>
			<MultipleSelect
				label="Reinforcers Used in Sessions: "
				data={reinforsmentList}
				setValue={reinforcerHandler}
				initialValue={data.reinforcementResponses}
				className="marginBottom16"
				disabled={getFieldDisabled('reinforcementResponses')}
				onBlur={save}
			/>
			{isOtherSelectedForReinforcers && (
				<CustomInput
					label="If other, please describe: "
					value={data.reinforcementOther}
					onChange={reinforcersOtherHandler}
					className="marginBottom16"
					multiline
					disabled={getFieldDisabled('reinforcementOther')}
					onBlur={save}
					error={errors.ReinforcementOther}
				/>
			)}
			<CustomInput
				label="Describe 3 goals that were addressed in the session:"
				value={data.workedGoals}
				onChange={workedGoalsHandler}
				className="marginBottom16"
				multiline
				disabled={getFieldDisabled('workedGoals')}
				onBlur={save}
			/>
			<CustomSelect
				label="Level of maladaptive behavior:"
				data={levelOfMaladaptiveBehaviorList}
				value={`${data.levelOfMaladaptiveBehavior}`}
				setValue={levelOfbehaviorHandler}
				className="marginBottom16"
				disabled={getFieldDisabled('levelOfMaladaptiveBehavior')}
				onBlur={save}
			/>
			<MultipleSelect
				label="Maladaptive behavior techniques used:"
				data={maladaptiveBehaviorTechniquesList}
				setValue={malaptiveTechniquesHandler}
				className="marginBottom16"
				initialValue={data.maladaptiveBehaviorTechniquesResponses}
				disabled={getFieldDisabled('maladaptiveBehaviorTechniquesResponses')}
				onBlur={save}
			/>
			<CustomInput
				label="Describe the frequency and duration of the maladaptive behavior and the techniques used as an intervention:"
				value={data.frequencyAndDurationOfMaladaptiveBehaviors}
				onChange={describeFrequencyAndDurationHandler}
				className="marginBottom16"
				multiline
				description={frequencyAndDurationDescription}
				disabled={getFieldDisabled('frequencyAndDurationOfMaladaptiveBehaviors')}
				onBlur={save}
			/>

			<MultipleSelect
				label="Client mood:"
				data={clientMoodList}
				setValue={clientMoodHandler}
				className="marginBottom16"
				initialValue={data.clientMoodResponses}
				disabled={getFieldDisabled('clientMoodResponses')}
				onBlur={save}
			/>
			{isOtherSelectedForClientMood && (
				<CustomInput
					label="If other, please describe: "
					value={data.clientMoodOther}
					onChange={clientMoodOtherHandler}
					className="marginBottom16"
					multiline
					disabled={getFieldDisabled('clientMoodOther')}
					onBlur={save}
					error={errors.ClientMoodOther}
				/>
			)}
			<CustomSelect
				label="Client response to treatment:"
				data={clientResponseToTreatmentList}
				value={`${data.clientResponseToTreatment}`}
				setValue={responseToTreatmentHandler}
				className="marginBottom16"
				disabled={getFieldDisabled('clientResponseToTreatment')}
				onBlur={save}
			/>
			<MultipleSelect
				label="Plan for Future Session:"
				data={behaviorTreatmentFutureList}
				setValue={futurePlansHandler}
				initialValue={data.behaviorTreatmentFuturePlanResponses}
				className="marginBottom16"
				disabled={getFieldDisabled('behaviorTreatmentFuturePlanResponses')}
				onBlur={save}
			/>
			{isOtherSelectedForFuturePlans && (
				<CustomInput
					label="If other, please describe: "
					value={data.behaviorTreatmentFuturePlanOther}
					onChange={futurePlanOtherHandler}
					className="marginBottom16"
					multiline
					disabled={getFieldDisabled('behaviorTreatmentFuturePlanOther')}
					onBlur={save}
					error={errors.FuturePlanOther}
				/>
			)}
			<Text title="Session Details" type="h4" font="header" fontWeight="semiBold" className="marginBottom16 title" />
			<Targets />
			<Behaviors />
			<SignatureInput
				error={errors.Signature}
				value={data.signature}
				setValue={signatureHandler}
				disabled={getFieldDisabled('signature')}
				onBlur={validateSignatureHandler}
			/>
			<div>
				<Text title="Date of signature:" type="psm" fontWeight="semiBold" />
				<Text title={todayString} type="psm" />
			</div>
		</>
	);
};

export default BehaviorTreatmentData;
